// @rpg-unit/impact-cue-presets: impact cue presets (tier 3; composition framework, decision 0077), cut from spike
// 002's `content/tables/impact-cues.json`, `feedback.json` and the bursts of `emitters.json`. The action-game cue
// set: for each impact kind (light, heavy, skill, big, chip, parry, guardBreak, finisher) what each feedback channel
// does (hit-stop seconds, the struck body's flash, camera trauma and kick, particle bursts, a sound cue, rumble), the
// nine particle bursts the cues name, and the feedback's own numbers (the camera shake's shape, weapon trails, the
// flash colour, which outcomes stay silent).
//
// Kinds are the standard `impact` event's `kind` (@rpg/contracts ImpactEvent); a game whose impacts use these kinds
// takes the set as it is. A game takes the presets three ways:
//   - as content: list this package in its content folder's `include.json` (@rpg/content reads `src/content`) or add
//     `IMPACT_CUE_CONTENT` to the tables it loads in the browser: the `impact-cues`, `impact-emitters` and `feedback`
//     tables;
//   - as @rpg/vfx and @rpg/camera values: `cueTable()` (the router's kind → channel → cue), `emitterSpecs()`,
//     `feedbackFeel()` (shake settings, trails, flash colour, silent outcomes);
//   - tuned by perceptual knobs: `tuneCues(rows, { punch, hold })`.
import type { ShakeSettings } from "@rpg/camera";
import type { CueTable, EmitterSpec } from "@rpg/vfx";
import cueTableFile from "./content/tables/impact-cues.json";
import cueSchema from "./content/schemas/impact-cues.schema.json";
import emitterTableFile from "./content/tables/impact-emitters.json";
import emitterSchema from "./content/schemas/impact-emitters.schema.json";
import feedbackTableFile from "./content/tables/feedback.json";
import feedbackSchema from "./content/schemas/feedback.schema.json";

/** This unit's version (the manifest's and package.json's). */
export const IMPACT_CUE_PRESETS_VERSION = "0.1.0" as const;

/** A reference to another table's row, as @rpg/content stores it. */
export interface RowRef {
  table: string;
  id: string;
}

/** One kind's cue row (`src/content/schemas/impact-cues.schema.json`): the channels it fires and their values. */
export interface ImpactCuePreset {
  id: string;
  hitStop?: { seconds: number };
  flash?: { peak: number; life: number };
  shake?: { trauma: number };
  kick?: { perSeverity: number };
  particles?: { emitter: RowRef; extra?: RowRef };
  sound?: { cue: string };
  rumble?: { low: number; high: number; seconds: number };
  reason: string;
}

/** A particle burst a cue names: @rpg/vfx's EmitterSpec with its id and reason. */
export type ImpactEmitterPreset = EmitterSpec & { id: string; reason: string };

/** The feedback's own numbers (one row, `default`). */
export interface FeedbackPreset {
  id: string;
  /** @rpg/camera's shake and kick shape. */
  shake: ShakeSettings;
  /** Weapon trails: samples kept, a sample's life (s, sim time), the colour. */
  trails: { samples: number; life: number; colour: string };
  /** The struck body's flash colour. */
  flashColour: string;
  /** Impact outcomes that fan out to nothing. */
  silentOutcomes: string[];
  reason: string;
}

/** The cue table (`{ table: "impact-cues", version, rows }`). */
export const IMPACT_CUES_TABLE = cueTableFile as { table: "impact-cues"; version: number; rows: ImpactCuePreset[] };
/** The bursts the cues name (`{ table: "impact-emitters", … }`). */
export const IMPACT_EMITTERS_TABLE = emitterTableFile as unknown as { table: "impact-emitters"; version: number; rows: ImpactEmitterPreset[] };
/** The feedback's own numbers (`{ table: "feedback", … }`). */
export const FEEDBACK_TABLE = feedbackTableFile as { table: "feedback"; version: number; rows: FeedbackPreset[] };

/** The content this unit adds to a game, in @rpg/content's shape: its tables and their schemas by table name. */
export const IMPACT_CUE_CONTENT = {
  package: "@rpg-unit/impact-cue-presets",
  version: IMPACT_CUE_PRESETS_VERSION,
  tables: [IMPACT_CUES_TABLE, IMPACT_EMITTERS_TABLE, FEEDBACK_TABLE],
  schemas: { "impact-cues": cueSchema as Record<string, unknown>, "impact-emitters": emitterSchema as Record<string, unknown>, feedback: feedbackSchema as Record<string, unknown> },
} as const;

/** The impact kinds the set covers, in table order. */
export const IMPACT_KINDS = IMPACT_CUES_TABLE.rows.map((r) => r.id);

/** The channels a cue row may name; `rumble` has no sink in a game without haptics (the router counts it). */
export const CUE_CHANNELS = ["hitStop", "flash", "shake", "kick", "particles", "sound", "rumble"] as const;

/** The cue rows as @rpg/vfx's router table: kind → channel → cue (particles carry emitter ids). */
export function cueTable(rows: readonly ImpactCuePreset[] = IMPACT_CUES_TABLE.rows): CueTable {
  const cues: Record<string, Record<string, Record<string, unknown>>> = {};
  for (const r of rows) {
    const { id, reason: _r, particles, ...rest } = r;
    const row: Record<string, Record<string, unknown>> = { ...rest };
    if (particles) row.particles = { emitter: particles.emitter.id, ...(particles.extra ? { extra: particles.extra.id } : {}) };
    cues[id] = row;
  }
  return cues;
}

/** The bursts as @rpg/vfx emitter specs by id. */
export function emitterSpecs(rows: readonly ImpactEmitterPreset[] = IMPACT_EMITTERS_TABLE.rows): Record<string, EmitterSpec> {
  const out: Record<string, EmitterSpec> = {};
  for (const e of rows) {
    const { id, reason: _r, ...spec } = e;
    out[id] = spec as EmitterSpec;
  }
  return out;
}

/** The feedback's own numbers from a row (default: the `default` row). */
export function feedbackFeel(row: FeedbackPreset | undefined = FEEDBACK_TABLE.rows.find((r) => r.id === "default")): FeedbackPreset {
  if (!row) throw new Error("impact cues: the feedback table has no 'default' row");
  return row;
}

/** The perceptual knobs (rule 13): what a person would say, and what each changes. */
export const IMPACT_CUE_KNOBS = {
  punch: "punchier or softer: scales how hard each hit lands on the senses: camera trauma, the kick, the flash's peak and the rumble (1 as tuned; 0.5 half; capped at each schema maximum).",
  hold: "longer or shorter holds: scales the hit-stop seconds and the flash's fade (1 as tuned; 0 removes hit-stop).",
} as const;

/** Knob values; each defaults to 1 (as tuned). */
export interface ImpactKnobs {
  punch?: number;
  hold?: number;
}

const round = (x: number): number => Math.round(x * 1e6) / 1e6;
const cap = (x: number, hi: number): number => round(Math.min(hi, x));

/** The cues turned by the knobs (new rows; the input is untouched). With no knobs, or both at 1, equal to the input. */
export function tuneCues(rows: readonly ImpactCuePreset[], knobs: ImpactKnobs = {}): ImpactCuePreset[] {
  const punch = knobs.punch ?? 1;
  const hold = knobs.hold ?? 1;
  if (!(punch >= 0) || !(hold >= 0)) throw new Error(`impact cues: punch and hold must be ≥ 0 (got ${punch}, ${hold})`);
  return rows.map((r) => {
    const out: ImpactCuePreset = structuredClone(r);
    if (punch !== 1) {
      if (out.shake) out.shake.trauma = cap(out.shake.trauma * punch, 1);
      if (out.kick) out.kick.perSeverity = cap(out.kick.perSeverity * punch, 1);
      if (out.flash) out.flash.peak = cap(out.flash.peak * punch, 1);
      if (out.rumble) out.rumble = { ...out.rumble, low: cap(out.rumble.low * punch, 1), high: cap(out.rumble.high * punch, 1) };
    }
    if (hold !== 1) {
      if (out.hitStop) out.hitStop.seconds = cap(out.hitStop.seconds * hold, 0.5);
      if (out.flash) out.flash.life = Math.max(0.01, cap(out.flash.life * hold, 2));
    }
    return out;
  });
}

/**
 * What's wrong with how the cue set grades its kinds (empty: none). An action game's hits must read in order of
 * weight: chip < light < skill < heavy < big in hit-stop, trauma and flash (where both have one); a parry and a guard
 * break hold at least as long as a heavy hit (they are rewards and punishments); every kind the router may get has a
 * row. Kinds a set doesn't have are skipped, so a gentler set (cozy pops) can use the same check on what it has.
 */
export function gradingIssues(rows: readonly ImpactCuePreset[]): string[] {
  const out: string[] = [];
  const by = new Map(rows.map((r) => [r.id, r]));
  const ladder = ["chip", "light", "skill", "heavy", "big"].filter((k) => by.has(k));
  const measures: [string, (r: ImpactCuePreset) => number | undefined][] = [
    ["hit-stop", (r) => r.hitStop?.seconds],
    ["trauma", (r) => r.shake?.trauma],
    ["flash", (r) => r.flash?.peak],
  ];
  for (const [name, of] of measures) {
    for (let i = 1; i < ladder.length; i++) {
      const a = of(by.get(ladder[i - 1]!)!);
      const b = of(by.get(ladder[i]!)!);
      if (a !== undefined && b !== undefined && !(b > a)) out.push(`${ladder[i]}'s ${name} ${b} is not above ${ladder[i - 1]}'s ${a}`);
    }
  }
  const heavy = by.get("heavy")?.hitStop?.seconds;
  for (const k of ["parry", "guardBreak"]) {
    const s = by.get(k)?.hitStop?.seconds;
    if (heavy !== undefined && s !== undefined && s < heavy) out.push(`${k}'s hit-stop ${s} is shorter than heavy's ${heavy}`);
  }
  return out;
}
